import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { useMemo } from "react";
import { AppHeader } from "~/components/layout/app-header";
import {
  ProjectContext,
  type ProjectContextValue,
} from "~/components/layout/project-context";
import { ProjectSidebar } from "~/components/layout/project-sidebar";
import { SidebarInset, SidebarProvider } from "~/components/ui/sidebar";
import { projectQueryOptions } from "~/lib/projects/queries";
import { conflictCountsQueryOptions } from "~/lib/screening/queries";

export const Route = createFileRoute("/_auth/projects/$projectId")({
  loader: async ({ context, params }) => {
    const project = await context.queryClient.ensureQueryData(
      projectQueryOptions(params.projectId),
    );
    // Prefetch conflict counts for sidebar badges (non-blocking)
    void context.queryClient.prefetchQuery(
      conflictCountsQueryOptions(params.projectId),
    );
    return { project };
  },
  head: ({ loaderData }) => ({
    meta: [{ title: loaderData ? `${loaderData.project.title} | Canopy` : "Canopy" }],
  }),
  component: ProjectLayout,
});

const segmentLabels: Record<string, string> = {
  citations: "Citations",
  settings: "Settings",
  import: "Import",
  statistics: "Statistics",
  duplicates: "Duplicates",
  screening: "Screening",
  "title-abstract": "Title & Abstract",
  "full-text": "Full Text",
  review: "Review",
  conflicts: "Conflicts",
  "conflicts-v1": "Conflicts",
  "conflicts-v2": "Conflicts",
  "conflicts-v3": "Conflicts",
  adjudication: "Adjudication",
};

function usePageBreadcrumbs(projectId: string) {
  const { pathname } = useLocation();

  return useMemo(() => {
    const suffix = pathname
      .replace(`/projects/${projectId}`, "")
      .replace(/^\//, "")
      .replace(/\/$/, "");

    if (!suffix) return [{ label: "Overview" }];

    return suffix.split("/").map((segment) => ({
      label: segmentLabels[segment] || segment,
    }));
  }, [pathname, projectId]);
}

function ProjectLayout() {
  const { projectId } = Route.useParams();
  const { user } = Route.useRouteContext();
  const { data: project } = useSuspenseQuery(projectQueryOptions(projectId));
  const pageItems = usePageBreadcrumbs(projectId);

  const contextValue: ProjectContextValue = {
    projectId,
    projectTitle: project.title,
    projectDescription: project.description,
    isOwner: project.isOwner,
    userId: user.id,
    userName: user.name,
    userEmail: user.email,
    userImage: user.image,
  };

  return (
    <ProjectContext.Provider value={contextValue}>
      <SidebarProvider>
        <ProjectSidebar
          projectId={projectId}
          projectTitle={project.title}
          isOwner={project.isOwner}
        />
        <SidebarInset>
          <AppHeader
            userName={user.name}
            userEmail={user.email}
            userImage={user.image}
            breadcrumbs={{
              projectId,
              projectTitle: project.title,
              items: pageItems,
            }}
          />
          <Outlet />
        </SidebarInset>
      </SidebarProvider>
    </ProjectContext.Provider>
  );
}
